import {
  TemplateStructure,
  V1TemplateStructure,
  TEMPLATE_VERSION_V2,
  LabelElement,
  isAdvancedTableElement,
  PageSize,
} from '../types';

/**
 * Ensures that any template, old or new, is converted to the new multi-page format.
 * This guarantees a consistent data structure for rendering and saving.
 *
 * @param templateData The template data, which can be in the old V1 or new V2 format.
 * @returns A template object conforming to the new `TemplateStructure`.
 */
export const normalizeTemplate = (
  templateData: TemplateStructure | V1TemplateStructure
): TemplateStructure => {
  // If it already has 'pages', it's the new format. Just ensure margins and footer settings are defaulted.
  if ('pages' in templateData) {
    return {
      ...templateData,
      marginTop: templateData.marginTop ?? 0,
      marginBottom: templateData.marginBottom ?? 0,
      marginLeft: templateData.marginLeft ?? 0,
      marginRight: templateData.marginRight ?? 0,
      footerEnabled: templateData.footerEnabled ?? false,
      footerHeight: templateData.footerHeight ?? 80,
    };
  }

  // If it's the legacy V1 format, convert it.
  return {
    version: TEMPLATE_VERSION_V2,
    pages: [{ elements: templateData.elements }],
    headerElements: [],
    footerElements: [],
    pageSize: templateData.pageSize,
    marginTop: 0,
    marginBottom: 0,
    marginLeft: 0,
    marginRight: 0,
    footerEnabled: false,
    footerHeight: 80,
  };
};

/**
 * Returns an array of elements with footer elements marked
 *
 * @param elements Array of label elements to check
 * @param pageSize The page size configuration
 * @param footerHeight Height of the footer in pixels
 * @param totalPages Total number of pages in the template
 * @returns Element array containing the elements in the footer area
 */
export const markedFooterElements = (
  elements: LabelElement[],
  pageSize: PageSize,
  footerHeight: number,
  totalPages: number
): LabelElement[] => {
  const singlePageHeight = pageSize.height;

  const markedElements: LabelElement[] = elements.map((element) => {
    const pageY = (totalPages - 1) * singlePageHeight;
    const footerStartY = pageY + singlePageHeight - footerHeight;

    // Defensively handle missing heights to prevent NaN comparisons
    const height = Number.isFinite(element.height) ? element.height : 0;

    // Check if element intersects with footer area (only for elements with valid height)
    const elementBottom = element.y + height;
    if (element.y >= footerStartY || elementBottom >= footerStartY) {
      return { ...element, footerElement: true };
    }
    return element;
  });

  return markedElements;
};

/**
 * Detects if the footer area is currently intersecting a table
 *
 * @param elements Array of LabelElements
 * @param footerPage Page number footer is on
 * @param footerHeight Height of the footer in pixels
 * @param pageSize The page size configuration
 * @returns Boolean indicating if table intersects footer
 */
export const footerIntersectsTable = (
  elements: LabelElement[],
  footerPage: number,
  footerHeight: number,
  pageSize: PageSize
): boolean => {
  const tableElements = elements.filter(
    (element) => element.type === 'advancedTable'
  );

  //get the footer y range
  const footerStartY = (footerPage + 1) * pageSize.height - footerHeight;
  const footerEndY = (footerPage + 1) * pageSize.height;

  let footerIntersects = false;

  tableElements.forEach((table) => {
    const tableEndY = table.y + (table.previewHeight || table.height);
    if (footerStartY < tableEndY && tableEndY <= footerEndY) {
      footerIntersects = true;
    }
  });
  return footerIntersects;
};

/**
 * Checks if there is room for a footer on the last page of the canvas
 * by examining only advancedTable elements that can grow dynamically
 *
 * @param footerHeight Height of footer in pixels
 * @param totalPages Number of pages
 * @param pageSize The page size configuration
 * @param elements Array of label elements
 * @returns Boolean indicating if there's room for the footer
 */
export const lastPageHasRoomForFooter = (
  footerHeight: number,
  totalPages: number,
  pageSize: PageSize,
  elements: LabelElement[]
): boolean => {
  // Filter to only advanced table elements since they can grow
  const advancedTableElements = elements.filter(
    (element) =>
      isAdvancedTableElement(element) && !element.id.includes('_page_')
  );

  // Calculate footer area boundaries on the last page
  const lastPageStartY = (totalPages - 1) * pageSize.height;
  const footerStartY = lastPageStartY + pageSize.height - footerHeight;

  // Check if any advanced table element would conflict with the footer area
  for (const table of advancedTableElements) {
    const tableBottomY = table.y + (table.previewHeight || table.height);

    // If table extends into or beyond the footer area, there's no room
    if (tableBottomY > footerStartY) {
      return false;
    }
  }

  return true;
};

/**
 * Updates footer elements' Y position to be at the bottom of the last page.
 * This function should be called after all pages have been created.
 *
 * @param elements Array of footer elements to update
 * @param pageSize The page size configuration
 * @param totalPages Total number of pages in the template
 * @param footerPage Page which contains the footer
 * @returns Updated footer elements with correct Y positions
 */
export const elementsWithUpdatedFooterPositionsOnLastPage = (
  elements: LabelElement[],
  pageSize: PageSize,
  totalPages: number,
  footerPage: number
): LabelElement[] => {
  const numberOfPagesToMove =
    totalPages > 1 ? totalPages - (footerPage + 1) : 0;

  return elements.map((element) => {
    if (element.footerElement) {
      return {
        ...element,
        y: element.y + numberOfPagesToMove * pageSize.height,
      };
    } else return element;
  });
};

/**
 * Find which page contains the first footer element.

 * @param {LabelElement[]} elements - Array of LabelElements
 * @param {PageSize} pageSize - Page size object.
 * @param {number} totalPages - Total number of pages.
 * @returns {number} 0-based page number containing the first footer element; `0` if none found.
 *
 */
export function findFooterPage(
  elements: LabelElement[],
  pageSize: PageSize,
  totalPages: number
): number {
  for (const element of elements) {
    if (!element?.footerElement) continue;

    const rawIndex = Math.floor(element.y / pageSize.height);
    return Math.max(0, Math.min(totalPages - 1, rawIndex));
  }

  return 0;
}
